// src/components/auth/CombinedLandingLogin.js
import React, { useState, useEffect } from 'react';
import { keyframes } from '@emotion/react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Microsoft,
  Visibility,
  VisibilityOff,
  Lock,
  Email
} from '@mui/icons-material';
import { useAuth } from '../utils/AuthContext';
import { API_PREFIX } from '../services/api';

// Mirrors the backend's ALLOWED_SSO_DOMAINS (routes/auth.py). Display only —
// the server enforces the actual restriction on Administrator signup.
const ADMIN_EMAIL_DOMAINS = (
  process.env.REACT_APP_ADMIN_EMAIL_DOMAINS || '2sdtechnologies.com,twosdtechnologies.com'
)
  .split(',')
  .map((d) => d.trim())
  .filter(Boolean);

 
// Animations
const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;
 
const CombinedLandingLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, checkAuthStatus } = useAuth();
 
  // Form state
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loginErr, setLoginErr] = useState('');
  const [loginLoad, setLoginLoad] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
 
  // Microsoft SSO state
  const [msLoad, setMsLoad] = useState(false);
 
  // Signup dialog state (for legacy users)
  const [suOpen, setSuOpen] = useState(false);
  const [suData, setSuData] = useState({ email: '', password: '', role: 'vendor' });
  const [suConf, setSuConf] = useState('');
  const [suErr, setSuErr] = useState('');
  const [suLoad, setSuLoad] = useState(false);
 
  // Error notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
 
  // Handle Microsoft SSO callback errors
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
   
    if (error) {
      const errorMessages = {
        'user_not_found': 'Your Microsoft account is not registered with TalentMesh. Please contact your administrator to get pre-assigned access or use traditional login.',
        'sso_not_configured': 'Microsoft SSO is not properly configured. Please try traditional login or contact support.',
        'incomplete_user_data': 'Microsoft profile is incomplete. Please try again or contact administrator.',
        'callback_failed': 'Microsoft authentication failed. Please try again.',
        'access_denied': 'You denied access to the application. Please try again and allow access.',
        'invalid_request': 'Invalid authentication request. Please try again.'
      };
     
      const message = errorMessages[error] || `Authentication error: ${error}`;
      setSnackbar({ open: true, message, severity: 'error' });
     
      // Clean URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);
 
  // Traditional login form handlers
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
 
  const signIn = async (e) => {
    e.preventDefault();
    setLoginErr('');
    setLoginLoad(true);
 
    try {
      const response = await login(formData.email, formData.password, remember);
     
      if (!response.success) {
        setLoginErr(response.message || 'Invalid credentials');
      } else {
        // Success - AuthContext will handle redirection based on user role
        console.log('✅ Traditional login successful');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginErr('Login failed. Please try again.');
    } finally {
      setLoginLoad(false);
    }
  };
 
  // Single Microsoft SSO login handler - no role hint needed
  const msLogin = async () => {
    if (msLoad) return; // Prevent double-clicks
   
    setMsLoad(true);
    console.log('🔐 Starting Microsoft SSO login...');
 
    try {
      // Call the Microsoft login endpoint without role parameter
      // Backend will determine role based on email domain and pre-assignment
      const response = await fetch(`${API_PREFIX}/auth/microsoft/login`, {
        method: 'GET',
        credentials: 'include',
      });
 
      if (response.ok) {
        const data = await response.json();
       
        if (data.auth_url) {
          // Redirect to Microsoft authentication
          console.log('🚀 Redirecting to Microsoft authentication...');
          window.location.href = data.auth_url;
          return; // Don't reset loading state - we're navigating away
        }
      }
     
      // If no auth_url, try direct redirect as fallback
      console.log('🔄 Fallback: Direct redirect to Microsoft login...');
      window.location.href = `${API_PREFIX}/auth/microsoft/login`;
     
    } catch (error) {
      console.error('Microsoft SSO error:', error);
      setSnackbar({
        open: true,
        message: 'Microsoft authentication is currently unavailable. Please try traditional login or contact support.',
        severity: 'error'
      });
      setMsLoad(false);
    }
  };
 
  // Signup handler (for legacy users)
  const createAcct = async () => {
    setSuErr('');
    setSuLoad(true);
 
    // Client-side validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(suData.email)) {
      setSuErr('Please enter a valid email address');
      setSuLoad(false);
      return;
    }
 
    if (suData.password.length < 6) {
      setSuErr('Password must be at least 6 characters');
      setSuLoad(false);
      return;
    }
 
    if (suData.password !== suConf) {
      setSuErr('Passwords do not match');
      setSuLoad(false);
      return;
    }
 
    try {
      const response = await fetch(`${API_PREFIX}/auth/signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...suData, remember: true })
      });
 
      const data = await response.json();
 
      if (response.ok && data.status === 'success') {
        // Refresh auth status and redirect
        await checkAuthStatus();
        setSuOpen(false);
       
        // Navigate to appropriate dashboard
        const roleRoutes = {
          admin: '/admin/dashboard',
          manager: '/manager/dashboard',
          vendor: '/vendor/dashboard',
          panelist: '/panelist/dashboard'
        };
       
        navigate(roleRoutes[data.user?.role] || '/');
      } else {
        setSuErr(data.error || 'Account creation failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setSuErr('Account creation failed. Please try again.');
    } finally {
      setSuLoad(false);
    }
  };
 
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 500
        }}
      >
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 4, width: '100%' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: 'white',
              mb: 2,
              animation: `${fadeInUp} 0.8s ease-out`
            }}
          >
            Welcome to TalentMesh
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: 1.6,
              maxWidth: 500,
              mx: 'auto'
            }}
          >
            Streamline your IT recruitment process with role-based access for administrators, vendors, and interview panels.
          </Typography>
        </Box>

        {/* Login Card */}
        <Paper
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 450,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography variant="h5" sx={{ textAlign: 'center', mb: 3, fontWeight: 600 }}>
            Sign In
          </Typography>
 
          {/* Traditional Login Form */}
          <Box component="form" onSubmit={signIn}>
            {loginErr && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {loginErr}
              </Alert>
            )}
 
            <TextField
              fullWidth
              required
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              sx={{ mb: 2 }}
              disabled={loginLoad || msLoad}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                )
              }}
            />
 
            <TextField
              fullWidth
              required
              name="password"
              label="Password"
              type={showPwd ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              sx={{ mb: 2 }}
              disabled={loginLoad || msLoad}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPwd(!showPwd)}
                      edge="end"
                      disabled={loginLoad || msLoad}
                    >
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
 
            <FormControlLabel
              control={
                <Checkbox
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={loginLoad || msLoad}
                />
              }
              label="Keep me signed in"
              sx={{ mb: 2 }}
            />
 
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loginLoad || msLoad}
              sx={{ mb: 2, py: 1.5 }}
            >
              {loginLoad ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>
 
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Divider>
 
          {/* Single Microsoft SSO Button */}
          <Button
            fullWidth
            variant="contained"
            startIcon={msLoad ? <CircularProgress size={20} color="inherit" /> : <Microsoft />}
            onClick={msLogin}
            disabled={msLoad || loginLoad}
            sx={{
              background: '#0078d4',
              '&:hover': { background: '#106ebe' },
              py: 1.5,
              mb: 2,
              fontWeight: 600
            }}
          >
            {msLoad ? 'Connecting...' : 'Sign in with Microsoft'}
          </Button>
 
          {/* Informational text */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              mb: 2,
              lineHeight: 1.4
            }}
          >
            Microsoft users will be automatically assigned to their pre-configured role
          </Typography>
 
          {/* Create Account Link */}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setSuOpen(true)}
            disabled={loginLoad || msLoad}
            sx={{ py: 1.5 }}
          >
            Create New Account
          </Button>
        </Paper>
      </Box>

      {/* Signup Dialog */}
      <Dialog
        open={suOpen}
        onClose={() => setSuOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          Create New Account
        </DialogTitle>
        <DialogContent>
          {suErr && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {suErr}
            </Alert>
          )}
 
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            type="email"
            value={suData.email}
            onChange={(e) => setSuData({ ...suData, email: e.target.value })}
            disabled={suLoad}
          />
 
          <TextField
            fullWidth
            margin="normal"
            label="Password"
            type="password"
            value={suData.password}
            onChange={(e) => setSuData({ ...suData, password: e.target.value })}
            disabled={suLoad}
          />
 
          <TextField
            fullWidth
            margin="normal"
            label="Confirm Password"
            type="password"
            value={suConf}
            onChange={(e) => setSuConf(e.target.value)}
            disabled={suLoad}
          />
 
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={suData.role}
              onChange={(e) => setSuData({ ...suData, role: e.target.value })}
              disabled={suLoad}
            >
              <MenuItem value="vendor">Vendor / HR</MenuItem>
              <MenuItem value="panelist">Panelist</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
            </Select>
            {/* The server is the authority here: it rejects Administrator for
                any address outside the internal domains. This only tells the
                user before they submit. */}
            {suData.role === 'admin' && (
              <FormHelperText>
                Requires a {ADMIN_EMAIL_DOMAINS.join(' or ')} email address.
              </FormHelperText>
            )}
          </FormControl>
        </DialogContent>
 
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setSuOpen(false)} disabled={suLoad}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createAcct}
            disabled={suLoad}
          >
            {suLoad ? <CircularProgress size={20} /> : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>
 
      {/* Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
 
export default CombinedLandingLogin;